import React, { useState } from "react";
import { useContractTypes } from "@/hooks/useContractTypes";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ContractTypeForm } from "@/components/contract-types/ContractTypeForm";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { PaymentFrequencyUnit, ContractDirection } from "@/lib/types";
import { ContractTypeModal } from "@/components/contract-types/ContractTypeModal";
import { getDirectionIcon } from "@/components/contract-types/ContractDirectionIcons";
import { useTranslation } from "react-i18next";

export function ContractTypesStep() {
  const [isAddingDefaults, setIsAddingDefaults] = useState(false);
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const { useContractTypesQuery, useCreateContractTypeMutation } = useContractTypes();
  const { data: contractTypes = [], isLoading } = useContractTypesQuery();
  const createContractTypeMutation = useCreateContractTypeMutation();
  const { t } = useTranslation();
  
  const DEFAULT_CONTRACT_TYPES = [
    {
      name: t('contractTypes.subscription.name', "Subscription"),
      description: t('contractTypes.subscription.description', "A recurring contract with regular payments"),
      auto_renews: true,
      renewal_unit: "Month" as PaymentFrequencyUnit,
      renewal_interval: 1,
      direction: "outgoing" as ContractDirection
    },
    {
      name: t('contractTypes.serviceAgreement.name', "Service Agreement"),
      description: t('contractTypes.serviceAgreement.description', "Contract for providing services"),
      auto_renews: true,
      renewal_unit: "Year" as PaymentFrequencyUnit,
      renewal_interval: 1,
      direction: "outgoing" as ContractDirection
    },
    {
      name: t('contractTypes.license.name', "License Agreement"),
      description: t('contractTypes.license.description', "Contract for the use of intellectual property"),
      auto_renews: true,
      renewal_unit: "Year" as PaymentFrequencyUnit,
      renewal_interval: 1,
      direction: "outgoing" as ContractDirection
    },
    {
      name: t('contractTypes.onetime.name', "One-time Purchase"),
      description: t('contractTypes.onetime.description', "Single purchase without renewal"),
      auto_renews: false,
      direction: "outgoing" as ContractDirection
    },
    {
      name: t('contractTypes.lease.name', "Lease/Rental"),
      description: t('contractTypes.lease.description', "Contract for renting/leasing property or equipment"),
      auto_renews: true,
      renewal_unit: "Month" as PaymentFrequencyUnit,
      renewal_interval: 1,
      direction: "outgoing" as ContractDirection
    },
    {
      name: t('contractTypes.sales.name', "Sales Contract"),
      description: t('contractTypes.sales.description', "Contract for selling products or services"),
      auto_renews: false,
      direction: "incoming" as ContractDirection
    },
    {
      name: t('contractTypes.employment.name', "Employment Contract"),
      description: t('contractTypes.employment.description', "Contract with employees"),
      auto_renews: true,
      renewal_unit: "Year" as PaymentFrequencyUnit,
      renewal_interval: 1,
      direction: "employment" as ContractDirection
    }
  ];

  const handleAddDefaultTypes = async () => {
    setIsAddingDefaults(true);
    
    try {
      for (const typeData of DEFAULT_CONTRACT_TYPES) {
        await createContractTypeMutation.mutateAsync(typeData);
      }
      
      toast.success(t('defaultContractTypesAdded', "Default contract types added successfully"));
    } catch (error) {
      console.error("Error adding default contract types:", error);
      toast.error(t('errorAddingDefaultContractTypes', "Failed to add some default contract types"));
    } finally {
      setIsAddingDefaults(false);
    }
  };

  const handleContractTypeAdded = () => {
    toast.success("Contract type added successfully");
  };
  
  return (
    <div className="space-y-4">
      <p>
        {t('contractTypes.intro', "Contract types help categorize your agreements and apply consistent rules. Each type can have specific renewal settings and direction/purpose.")}
      </p>
      
      {isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : contractTypes.length === 0 ? (
        <div className="bg-muted rounded-lg p-6 text-center space-y-4">
          <p className="mb-4">{t('contractTypes.empty', "You don't have any contract types yet. Add default types or create your own.")}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              onClick={handleAddDefaultTypes} 
              disabled={isAddingDefaults}
              className="w-full sm:w-auto"
            >
              {isAddingDefaults ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('contractTypes.addingDefaults', "Adding defaults...")}
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('contractTypes.addDefaults', "Add Default Contract Types")}
                </>
              )}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setTypeModalOpen(true)} 
              className="w-full sm:w-auto"
            >
              <Plus className="mr-2 h-4 w-4" />
              {t('contractTypes.createManually', "Create Type Manually")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">{t('contractTypes.yourTypes', "Your Contract Types")}</CardTitle>
              <CardDescription>
                {t('contractTypes.count', "You have {{count}} contract type(s)", { count: contractTypes.length })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {contractTypes.map((type) => (
                  <li key={type.id} className="p-2 border rounded-md">
                    <div className="flex items-center gap-1.5">
                      {getDirectionIcon(type.direction)}
                      <span className="font-medium">{type.name}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">{type.description}</div>
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex justify-end">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setTypeModalOpen(true)}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add New Type
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      <ContractTypeModal
        open={typeModalOpen}
        onOpenChange={setTypeModalOpen}
        onSuccess={handleContractTypeAdded}
      />
    </div>
  );
}
